<template>
  <v-app class="bg-black text-white text-center">
    <v-main>
      <v-container>
        <v-sheet
          class="bg-black d-flex flex-column align-center"
          :class="isInspectionDeferred ? 'mb-2' : 'mb-8'"
        >
          <div v-if="isInspectionDeferred" class="my-4 deferred-emoji">🕰️</div>
          <v-img v-else class="my-10" src="@/assets/images/celebration.png" width="80px" />

          <h1 class="page-title px-1">
            {{ isInspectionDeferred ? $t("end.deferredTitle") : $t("end.title") }}
          </h1>
        </v-sheet>

        <v-sheet class="bg-black" :class="isInspectionDeferred ? 'mb-2' : 'mb-8'">
          <template v-if="isInspectionDeferred">
            <p class="mb-0">
              {{ $t("end.deferredDescription1") }}
            </p>
            <p v-if="!showFrame" class="mb-0">
              {{ $t("end.deferredDescription2") }}
            </p>
          </template>
          <template v-else>
            <p class="mb-0">
              {{ $t("end.description1") }}
            </p>
            <br />
            <p class="mb-0">
              {{ $t("end.description2", { email: client.email }) }}
            </p>
          </template>
        </v-sheet>

        <v-sheet
          v-if="isInspectionDeferred && showFrame"
          class="bg-black d-flex flex-column align-center mt-6 mb-6"
        >
          <QrcodeVue :value="resumeUrl" :size="180" />
          <div class="d-flex align-center ga-2 mt-2">
            <p class="text-caption mb-0">{{ $t("end.qrCodeSubtitle") }}</p>
            <v-btn
              :icon="mdiContentCopy"
              variant="text"
              color="white"
              size="20"
              class="pa-0"
              :aria-label="$t('end.copyLinkButton')"
              @click="copyResumeLink"
            />
          </div>
        </v-sheet>

        <v-card class="bg-black">
          <v-card-text>
            <p class="font-weight-bold mb-2">{{ $t("end.experienceTitle") }}</p>
            <p class="mb-0">{{ $t("end.experienceSubtitle") }}</p>
            <v-rating
              v-model="rating"
              active-color="#EFCE4A"
              class="text-center mt-2"
              item-label-position="bottom"
              :readonly="hasSubmittedRating"
              @click="onOpenRatingsModal"
            />
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <app-footer class="bg-dark">
      <footer-button color="white" variant="text" @click="onOpenWhatsApp">
        {{ $t("end.doubtsButton") }}
      </footer-button>
      <footer-button color="white" variant="outlined" @click="onClose">
        {{ $t("end.closeButton") }}
      </footer-button>
    </app-footer>

    <v-dialog v-model="isRatingsModalVisible" max-width="400px">
      <v-card class="pa-6 bg-light">
        <p class="mb-1">{{ $t("end.commentTitle") }}</p>

        <v-textarea
          v-model="comment"
          class="mb-6 comment bg-light"
          maxlength="255"
          :placeholder="$t('end.commentPlaceholder')"
          rows="4"
          variant="outlined"
          wrap="HARD"
        />

        <p class="mb-6 text-center">
          {{ $t("end.commentMessage") }}
        </p>

        <v-sheet class="d-flex flex-column">
          <app-button
            v-if="rating"
            color="primary"
            :loading="loading"
            size="large"
            @click="onSubmitRating"
          >
            {{ $t("end.sendButton") }}
          </app-button>
          <app-button
            class="mt-1"
            color="primary"
            :loading="loading"
            size="large"
            variant="text"
            @click="onSubmitRating"
          >
            {{ $t("end.sendWithoutCommentButton") }}
          </app-button>
        </v-sheet>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
  import confetti from "canvas-confetti";
  import { mdiContentCopy } from "@mdi/js";
  import { storeToRefs } from "pinia";
  import QrcodeVue from "qrcode.vue";
  import { computed, onMounted, ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";

  import { useDesktopFrame } from "@/composables/useDesktopFrame";
  import { useUtils } from "@/composables/useUtils";
  import { useApiStore } from "@/stores/api";
  import { showToast } from "@/utils/toast";

  const router = useRouter();
  const api = useApiStore();
  const util = useUtils();
  const { t } = useI18n();
  const { showFrame } = useDesktopFrame();

  const loading = ref(false);
  const isRatingsModalVisible = ref(false);
  const rating = ref(null);
  const comment = ref(null);
  const hasSubmittedRating = ref(false);

  const { client } = storeToRefs(api);

  const isInspectionDeferred = computed(() => api.inspectionDeferred && !api.inspectionCompleted);
  const resumeUrl = computed(() => `${window.location.origin}/${api.insuranceCode}`);

  onMounted(async () => {
    if (api.inspectionDeferred) {
      await api.getData();
    }
    if (api.inspectionDeferred && !api.inspectionCompleted) return;
    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.6 },
    });
  });

  function onOpenWhatsApp() {
    util.openBikeHeroWhatsApp();
  }

  async function copyResumeLink() {
    try {
      await navigator.clipboard.writeText(resumeUrl.value);
      showToast.success({
        title: t("end.copyLinkSuccessTitle"),
        description: t("end.copyLinkSuccessDescription"),
      });
    } catch (error) {
      showToast.warning({
        title: t("end.copyLinkErrorTitle"),
        description: t("end.copyLinkErrorDescription"),
      });
    }
  }

  function onClose() {
    router.push({ name: "home" });
    setTimeout(() => {
      api.$reset();
    }, 500);
  }

  function onOpenRatingsModal() {
    if (hasSubmittedRating.value) return;
    isRatingsModalVisible.value = true;
  }

  function onSubmitRating() {
    loading.value = true;
    api
      .sendRating(api.insuranceCode, rating.value, comment.value)
      .then(() => {
        hasSubmittedRating.value = true;
        isRatingsModalVisible.value = false;
      })
      .finally(() => {
        loading.value = false;
      });
  }
</script>

<style scoped lang="scss">
  .bg-dark {
    background-color: #121212 !important;
  }
  .bg-light {
    background-color: #f5f6f6 !important;
  }
  .comment {
    background-color: #f5f6f6;
    border-color: #e4e6e9;
    border-width: 1px;
  }
  .comment :deep(textarea) {
    font-size: 14px !important;
  }
  .page-title {
    font-size: 1.75rem;
    line-height: 1.25;
  }
  .deferred-emoji {
    font-size: 80px;
    line-height: 1;
  }
  :deep(.v-card--variant-outlined) {
    border-color: #ececec;
  }
  .v-input :deep(.v-input__details) {
    display: none;
  }
</style>
